import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import Modal from 'app/components/Modal/Modal';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {FeaturesActions} from 'app/providers/FeaturesProvider';
import {recordEvent} from 'app/tools/Analytics';
import {formatFeatureName} from 'app/utils/featureUtils';

export const GroupLocationsModal: React.FunctionComponent<
  React.PropsWithChildren<{
    isOpen: boolean;
    onClose: () => void;
    onDone: () => void;
    featureCollectionId: number;
    selectedFeatures: I.ImmutableOf<ApiFeature[]>;
    updateFeature: FeaturesActions['updateFeature'];
    setIsUpdatingFeatures: React.Dispatch<React.SetStateAction<boolean>>;
  }>
> = ({
  isOpen,
  onClose,
  onDone,
  featureCollectionId,
  selectedFeatures,
  updateFeature,
  setIsUpdatingFeatures,
}) => {
  const [groupName, setGroupName] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const handleClose = () => {
    setGroupName('');
    setError(null);
    onClose();
  };

  const onSubmit = async () => {
    const newName = groupName.trim();
    if (!newName) {
      return;
    }

    setError(null);
    setIsUpdatingFeatures(true);
    try {
      await Promise.all(
        selectedFeatures
          .filter((f) => !!f)
          .map(async (feature) => {
            const oldName = ((feature!.getIn(['properties', 'name']) as string) || '').trim();
            if (!oldName || oldName === newName) {
              return;
            }

            await updateFeature(
              feature!.get('id'),
              I.fromJS({properties: {name: newName, multiFeaturePartName: oldName}})
            );
          })
          .toArray()
      );

      recordEvent('Created multi-location property', {
        featureCollectionId,
        featureCount: selectedFeatures.size,
      });

      onDone();
      handleClose();
    } catch (e) {
      setError((e as Error)?.message || 'Failed to group locations. Please try again.');
    } finally {
      setIsUpdatingFeatures(false);
    }
  };

  const isDisabled = !groupName.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      headerText="Create multi-location property"
      primaryButton={{
        text: 'Create',
        isDisabled,
        onClick: onSubmit,
      }}
    >
      <div style={{marginTop: '2rem'}}>
        <B.FormGroup label="Property name">
          <B.InputGroup
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Enter a new for the new multi-location property"
          />
        </B.FormGroup>

        <B.Callout intent={B.Intent.PRIMARY} icon={null}>
          <p>
            Please confirm you want to group the following properties into a new{' '}
            <a href="https://lens.tech/support/article/31-multi-location-properties">
              multi-location property:
            </a>
          </p>
          <ul style={{listStyleType: 'unset', paddingLeft: '2rem'}}>
            {selectedFeatures
              .filter((f) => !!f)
              .toArray()
              .map((f) => {
                return <li key={String(f!.get('id'))}>{formatFeatureName(f!.toJS())}</li>;
              })}
          </ul>
        </B.Callout>

        {error && <B.Callout intent={B.Intent.DANGER}>{error}</B.Callout>}
      </div>
    </Modal>
  );
};
